import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Volume2, Plus, Music, Layers, Clock, Trash2, LogOut, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
    const navigate = useNavigate();
    const { user, logout, token } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showNewProject, setShowNewProject] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectType, setNewProjectType] = useState('single');
    const [creating, setCreating] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const response = await axios.get(`${API_URL}/projects`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setProjects(response.data);
        } catch (error) {
            console.error('Error fetching projects:', error);
            if (error.response?.status === 401) {
                logout();
                navigate('/login');
            } else {
                toast.error('Failed to load projects');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) {
            toast.error('Please enter a project name');
            return;
        }

        setCreating(true);
        try {
            const response = await axios.post(`${API_URL}/projects`, {
                name: newProjectName,
                project_type: newProjectType,
                tracks: [],
                settings: {}
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            toast.success('Project created!');
            setShowNewProject(false);
            setNewProjectName('');
            navigate(`/master/${response.data.id}`);
        } catch (error) {
            console.error('Error creating project:', error);
            toast.error('Failed to create project');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;

        setDeleting(true);
        try {
            await axios.delete(`${API_URL}/projects/${deleteId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setProjects(projects.filter(p => p.id !== deleteId));
            toast.success('Project deleted');
        } catch (error) {
            console.error('Error deleting project:', error);
            toast.error('Failed to delete project');
        } finally {
            setDeleting(false);
            setDeleteId(null);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="dashboard-page">
            {/* Header */}
            <header className="glass sticky top-0 z-50 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D0F224] flex items-center justify-center">
                            <Volume2 className="w-6 h-6 text-black" />
                        </div>
                        <span className="font-heading text-xl font-bold tracking-tight">AudioMaster Pro</span>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-sm text-[#A1A1AA]">{user?.username}</span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-6 py-12">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-bold mb-2">Your Projects</h1>
                        <p className="text-[#A1A1AA]">Manage your mastering projects</p>
                    </div>
                    <Button
                        className="bg-[#D0F224] text-black hover:bg-[#E2F568] font-bold"
                        onClick={() => setShowNewProject(true)}
                        data-testid="new-project-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Project
                    </Button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <Loader2 className="w-8 h-8 animate-spin text-[#D0F224]" />
                    </div>
                ) : projects.length === 0 ? (
                    <div className="text-center py-24">
                        <Music className="w-16 h-16 mx-auto mb-4 text-[#27272A]" />
                        <h2 className="font-heading text-xl font-bold mb-2">No projects yet</h2>
                        <p className="text-[#A1A1AA] mb-6">Create your first mastering project</p>
                        <Button
                            className="bg-[#D0F224] text-black hover:bg-[#E2F568] font-bold"
                            onClick={() => setShowNewProject(true)}
                            data-testid="create-first-project-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Project
                        </Button>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {projects.map((project) => (
                            <div 
                                key={project.id} 
                                className="project-card cursor-pointer"
                                onClick={() => navigate(`/master/${project.id}`)}
                                data-testid={`project-card-${project.id}`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                            project.project_type === 'multitrack' ? 'bg-[#00F0FF]/20' : 'bg-[#D0F224]/20'
                                        }`}>
                                            {project.project_type === 'multitrack' ? (
                                                <Layers className="w-5 h-5 text-[#00F0FF]" />
                                            ) : (
                                                <Music className="w-5 h-5 text-[#D0F224]" />
                                            )}
                                        </div>
                                        <div>
                                            <h3 className="font-heading font-bold">{project.name}</h3>
                                            <span className="text-xs text-[#A1A1AA] capitalize">{project.project_type}</span>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="text-[#A1A1AA] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteId(project.id);
                                        }}
                                        data-testid={`delete-project-${project.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>

                                <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                                    <Clock className="w-4 h-4" />
                                    {formatDate(project.updated_at)}
                                </div>

                                {project.tracks && project.tracks.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-[#27272A]">
                                        <p className="text-xs text-[#A1A1AA]">
                                            {project.tracks.length} track{project.tracks.length > 1 ? 's' : ''}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </main>

            {/* New Project Dialog */}
            <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
                <DialogContent className="bg-[#121212] border-[#27272A]" aria-describedby="new-project-description">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Create New Project</DialogTitle>
                        <p id="new-project-description" className="text-sm text-[#A1A1AA]">Set up your mastering project</p>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-white">Project Name</Label>
                            <Input
                                placeholder="My Track Master"
                                value={newProjectName}
                                onChange={(e) => setNewProjectName(e.target.value)}
                                className="bg-[#0A0A0A] border-[#333] focus:border-[#D0F224] focus:ring-[#D0F224]"
                                data-testid="project-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-white">Project Type</Label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setNewProjectType('single')}
                                    className={`p-4 rounded-lg border text-left transition-colors ${
                                        newProjectType === 'single'
                                            ? 'border-[#D0F224] bg-[#D0F224]/10'
                                            : 'border-[#27272A] hover:border-[#333]'
                                    }`}
                                    data-testid="single-track-btn"
                                >
                                    <Music className={`w-5 h-5 mb-2 ${newProjectType === 'single' ? 'text-[#D0F224]' : 'text-[#A1A1AA]'}`} />
                                    <div className="font-medium">Single Track</div>
                                    <p className="text-xs text-[#A1A1AA] mt-1">Master one audio file</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNewProjectType('multitrack')}
                                    className={`p-4 rounded-lg border text-left transition-colors ${
                                        newProjectType === 'multitrack'
                                            ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                                            : 'border-[#27272A] hover:border-[#333]'
                                    }`}
                                    data-testid="multitrack-btn"
                                >
                                    <Layers className={`w-5 h-5 mb-2 ${newProjectType === 'multitrack' ? 'text-[#00F0FF]' : 'text-[#A1A1AA]'}`} />
                                    <div className="font-medium">Multi-track</div>
                                    <p className="text-xs text-[#A1A1AA] mt-1">Mix multiple stems</p>
                                </button>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowNewProject(false)}
                            className="border-[#333]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateProject}
                            disabled={creating}
                            className="bg-[#D0F224] text-black hover:bg-[#E2F568]"
                            data-testid="create-project-btn"
                        >
                            {creating ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create Project'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading">Delete Project?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[#A1A1AA]">
                            This action cannot be undone. This will permanently delete your project.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="border-[#333] hover:bg-[#1E1E1E]">
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                'Delete'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
